import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Copy, Check, Loader2, Image as ImageIcon, Folder } from "lucide-react";

/**
 * Phase 25.6 — Media Library.
 *
 * One-stop shop for uploading hero images, logos, template artwork, etc.
 * to R2. Returns the public r2.dev URL, copyable to clipboard for pasting
 * into the Business Settings, page builder, email templates, etc.
 *
 * For now this is "stateless upload" — every upload mints a fresh R2 key
 * and shows the URL. We can wire a per-tenant index later if needed.
 */
const KIND_OPTIONS = [
  { value: "hero", label: "Hero image" },
  { value: "logo", label: "Logo" },
  { value: "template", label: "Template artwork" },
  { value: "social", label: "Social post" },
  { value: "menu", label: "Menu photo" },
  { value: "misc", label: "Other" },
];

export default function AdminMediaLibraryPage() {
  const [kind, setKind] = useState("hero");
  const [uploads, setUploads] = useState([]); // local in-memory log this session
  const [uploading, setUploading] = useState(false);
  const [copied, setCopied] = useState("");
  const fileInputRef = useRef(null);

  // Pull session uploads from sessionStorage so refresh doesn't wipe history.
  useEffect(() => {
    try {
      const stored = JSON.parse(sessionStorage.getItem("media-uploads") || "[]");
      if (Array.isArray(stored)) setUploads(stored);
    } catch (_) { /* ignore */ }
  }, []);

  const persist = (next) => {
    setUploads(next);
    try { sessionStorage.setItem("media-uploads", JSON.stringify(next.slice(0, 60))); } catch (_) {}
  };

  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    const next = [...uploads];
    for (const file of files) {
      if (file.size > 8 * 1024 * 1024) {
        toast.error(`${file.name} is over 8 MB — skipped`);
        continue;
      }
      try {
        const reader = new FileReader();
        // eslint-disable-next-line no-await-in-loop
        const dataUrl = await new Promise((res, rej) => {
          reader.onload = () => res(String(reader.result || ""));
          reader.onerror = rej;
          reader.readAsDataURL(file);
        });
        // eslint-disable-next-line no-await-in-loop
        const { data } = await api.post("/admin/assets/upload", { image: dataUrl, kind });
        next.unshift({
          url: data.public_url, key: data.key, kind, name: file.name,
          size: file.size, at: new Date().toISOString(),
        });
        toast.success(`Uploaded ${file.name}`);
      } catch (err) {
        toast.error(formatApiError(err?.response?.data?.detail) || `Failed: ${file.name}`);
      }
    }
    persist(next);
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const copy = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(url);
      toast.success("URL copied");
      setTimeout(() => setCopied(""), 1500);
    } catch (_) { /* noop */ }
  };

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto" data-testid="admin-media-page">
      <header className="mb-6 flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-sky-50 border border-sky-200 flex items-center justify-center">
          <Folder className="w-5 h-5 text-sky-700" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold text-stone-900">Media Library</h1>
          <p className="text-sm text-stone-500">
            Upload hero images, logos and artwork to Cloudflare R2. Copy the URL and paste it anywhere — settings, pages, email templates.
          </p>
        </div>
      </header>

      <div className="bg-white border border-stone-200 rounded-2xl p-5 mb-6">
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex-1 min-w-[180px]">
            <span className="text-xs uppercase tracking-widest text-stone-500">Category</span>
            <select
              value={kind} onChange={(e) => setKind(e.target.value)}
              data-testid="media-kind"
              className="mt-1 w-full rounded-lg border border-stone-200 px-3 py-2 text-sm bg-white"
            >
              {KIND_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>
          <label
            className="inline-flex items-center gap-2 bg-stone-900 text-white text-sm font-medium px-5 py-2.5 rounded-lg cursor-pointer hover:bg-stone-800"
            data-testid="media-upload-label"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            Upload images
            <input
              ref={fileInputRef}
              type="file" accept="image/*" multiple
              className="hidden"
              onChange={onPick}
              data-testid="media-upload-input"
            />
          </label>
        </div>
        <p className="text-[11px] text-stone-400 mt-3">
          Files are auto-resized to ≤ 2000px, GPS/EXIF stripped, and stored on Cloudflare R2.
          Direct upload limit 8 MB per file. Owner photos are <b>not</b> watermarked — customer photos are.
        </p>
      </div>

      <h2 className="text-sm font-semibold text-stone-900 mb-3">This session ({uploads.length})</h2>
      {uploads.length === 0 ? (
        <div className="text-sm text-stone-500 italic">
          No uploads yet — pick a category above and drop in your first image.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="media-grid">
          {uploads.map((u, i) => (
            <figure
              key={u.url + i}
              data-testid={`media-card-${i}`}
              className="bg-white border border-stone-200 rounded-2xl overflow-hidden"
            >
              <div className="relative aspect-square bg-stone-100">
                <img
                  src={u.url}
                  alt={u.name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  onContextMenu={(e) => e.preventDefault()}
                  draggable={false}
                />
                <span className="absolute top-2 left-2 text-[9px] uppercase tracking-widest bg-stone-900/85 text-white px-2 py-0.5 rounded-full">
                  {u.kind}
                </span>
              </div>
              <div className="p-3 text-xs space-y-1.5">
                <div className="text-stone-700 line-clamp-1" title={u.name}>{u.name}</div>
                <div className="text-stone-400 text-[10px]">{(u.size / 1024).toFixed(1)} KB</div>
                <button
                  onClick={() => copy(u.url)}
                  data-testid={`media-copy-${i}`}
                  className="w-full inline-flex items-center justify-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded-md bg-stone-50 border border-stone-200 hover:bg-stone-100"
                >
                  {copied === u.url ? <><Check className="w-3 h-3 text-emerald-600" /> Copied</>
                                     : <><Copy className="w-3 h-3" /> Copy URL</>}
                </button>
              </div>
            </figure>
          ))}
        </div>
      )}
    </div>
  );
}
